import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart, Cell } from 'recharts';
const WorkingCapitalDashboard: React.FC = () => {
  const kpis = [{
    title: 'Receivable Days',
    value: '58',
    change: '-5 days',
    isPositive: true,
    subtitle: 'Days Sales Outstanding'
  }, {
    title: 'Payable Days',
    value: '52',
    change: '+2 days',
    isPositive: true,
    subtitle: 'Days Payable Outstanding'
  }, {
    title: 'Inventory Days',
    value: '42',
    change: '-3 days',
    isPositive: true,
    subtitle: 'Days Inventory Outstanding'
  }, {
    title: 'Cash Conversion Cycle',
    value: '48',
    change: '-10 days',
    isPositive: true,
    subtitle: 'CCC Days'
  }];
  // AR Ageing Data
  const arAgeingData = [{
    bucket: '0-30 Days',
    amount: 785,
    percentage: 42.4,
    color: '#10b981'
  }, {
    bucket: '31-60 Days',
    amount: 542,
    percentage: 29.3,
    color: '#3b82f6'
  }, {
    bucket: '61-90 Days',
    amount: 318,
    percentage: 17.2,
    color: '#f59e0b'
  }, {
    bucket: '91-120 Days',
    amount: 142,
    percentage: 7.7,
    color: '#ef4444'
  }, {
    bucket: '>120 Days',
    amount: 63,
    percentage: 3.4,
    color: '#991b1b'
  }];
  // AP Payment Behaviour Data
  const apPaymentData = [{
    month: 'Jan',
    onTime: 425,
    early: 85,
    late: 35
  }, {
    month: 'Feb',
    onTime: 438,
    early: 92,
    late: 28
  }, {
    month: 'Mar',
    onTime: 445,
    early: 95,
    late: 25
  }, {
    month: 'Apr',
    onTime: 452,
    early: 88,
    late: 22
  }, {
    month: 'May',
    onTime: 465,
    early: 98,
    late: 18
  }, {
    month: 'Jun',
    onTime: 472,
    early: 102,
    late: 15
  }, {
    month: 'Jul',
    onTime: 478,
    early: 105,
    late: 12
  }, {
    month: 'Aug',
    onTime: 485,
    early: 108,
    late: 10
  }, {
    month: 'Sep',
    onTime: 492,
    early: 112,
    late: 8
  }, {
    month: 'Oct',
    onTime: 498,
    early: 115,
    late: 6
  }, {
    month: 'Nov',
    onTime: 505,
    early: 118,
    late: 5
  }, {
    month: 'Dec',
    onTime: 512,
    early: 122,
    late: 4
  }];
  // Inventory Days Trend
  const inventoryTrendData = [{
    month: 'Jan',
    rawMaterial: 28,
    wip: 12,
    finishedGoods: 15,
    total: 55
  }, {
    month: 'Feb',
    rawMaterial: 27,
    wip: 11,
    finishedGoods: 15,
    total: 53
  }, {
    month: 'Mar',
    rawMaterial: 26,
    wip: 11,
    finishedGoods: 14,
    total: 51
  }, {
    month: 'Apr',
    rawMaterial: 25,
    wip: 10,
    finishedGoods: 14,
    total: 49
  }, {
    month: 'May',
    rawMaterial: 25,
    wip: 10,
    finishedGoods: 13,
    total: 48
  }, {
    month: 'Jun',
    rawMaterial: 24,
    wip: 10,
    finishedGoods: 13,
    total: 47
  }, {
    month: 'Jul',
    rawMaterial: 24,
    wip: 9,
    finishedGoods: 13,
    total: 46
  }, {
    month: 'Aug',
    rawMaterial: 23,
    wip: 9,
    finishedGoods: 12,
    total: 44
  }, {
    month: 'Sep',
    rawMaterial: 23,
    wip: 9,
    finishedGoods: 12,
    total: 44
  }, {
    month: 'Oct',
    rawMaterial: 22,
    wip: 9,
    finishedGoods: 12,
    total: 43
  }, {
    month: 'Nov',
    rawMaterial: 22,
    wip: 8,
    finishedGoods: 12,
    total: 42
  }, {
    month: 'Dec',
    rawMaterial: 22,
    wip: 8,
    finishedGoods: 12,
    total: 42
  }];
  // Working Capital by Region Table
  const wcByRegionData = [{
    region: 'North',
    receivables: 542,
    receivableDays: 62,
    inventory: 385,
    inventoryDays: 45,
    payables: 428,
    payableDays: 50,
    netWC: 499,
    ccc: 57
  }, {
    region: 'West',
    receivables: 628,
    receivableDays: 55,
    inventory: 445,
    inventoryDays: 39,
    payables: 495,
    payableDays: 52,
    netWC: 578,
    ccc: 42
  }, {
    region: 'South',
    receivables: 412,
    receivableDays: 58,
    inventory: 298,
    inventoryDays: 42,
    payables: 335,
    payableDays: 51,
    netWC: 375,
    ccc: 49
  }, {
    region: 'East',
    receivables: 268,
    receivableDays: 65,
    inventory: 185,
    inventoryDays: 48,
    payables: 215,
    payableDays: 54,
    netWC: 238,
    ccc: 59
  }];
  return <div data-id="element-4680">
      <DescriptionPanel title="Working Capital Management" description="Comprehensive analysis of working capital components including accounts receivable, accounts payable, and inventory. Monitor cash conversion cycle, ageing patterns, and regional performance to optimize working capital efficiency and cash flow." insights={['Cash conversion cycle improved to 48 days from 58 days last year', 'Receivable days reduced to 58 from 63 - collection efficiency improving', 'Inventory optimization reduced days to 42 from 45 days', '42.4% of receivables in 0-30 day bucket - healthy collection pattern']} data-id="element-4681" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-4682">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-4683">
          Working Capital Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-4684">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-4685">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-4686">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-4687">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-4688">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-4689">
                {kpi.isPositive ? <TrendingDownIcon size={16} className="mr-1" data-id="element-4690" /> : <TrendingUpIcon size={16} className="mr-1" data-id="element-4691" />}
                {kpi.change} vs last year
              </div>
            </Card>)}
        </div>
      </div>
      {/* AR Ageing and AP Payment Behaviour */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-4692">
        {/* AR Ageing Bar Chart */}
        <Card className="p-6" data-id="element-4693">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-4694">
            Accounts Receivable Ageing
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-4695">
            Total AR: ₹1,850 Cr | Ageing breakdown
          </p>
          <ResponsiveContainer width="100%" height={300} data-id="element-4696">
            <BarChart data={arAgeingData} data-id="element-4697">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4698" />
              <XAxis dataKey="bucket" tick={{
              fontSize: 11
            }} data-id="element-4699" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-4700" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `₹${value} Cr`} data-id="element-4701" />
              <Bar dataKey="amount" name="Amount (₹ Cr)" data-id="element-4702">
                {arAgeingData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-4703" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-4704">
            <div className="grid grid-cols-5 gap-2" data-id="element-4705">
              {arAgeingData.map((item, index) => <div key={index} className="text-center" data-id="element-4706">
                  <div className="text-xs font-semibold mb-1" style={{
                color: item.color
              }} data-id="element-4707">
                    {item.bucket}
                  </div>
                  <div className="text-sm font-bold text-gray-900" data-id="element-4708">
                    {item.percentage}%
                  </div>
                </div>)}
            </div>
          </div>
        </Card>
        {/* AP Payment Behaviour */}
        <Card className="p-6" data-id="element-4709">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-4710">
            Accounts Payable Payment Behaviour
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-4711">
            Monthly payment patterns (₹ Cr)
          </p>
          <ResponsiveContainer width="100%" height={300} data-id="element-4712">
            <AreaChart data={apPaymentData} data-id="element-4713">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4714" />
              <XAxis dataKey="month" tick={{
              fontSize: 11
            }} data-id="element-4715" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-4716" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `₹${value} Cr`} data-id="element-4717" />
              <Legend data-id="element-4718" />
              <Area type="monotone" dataKey="early" stackId="1" stroke="#10b981" fill="#10b981" name="Early Payments" data-id="element-4719" />
              <Area type="monotone" dataKey="onTime" stackId="1" stroke="#3b82f6" fill="#3b82f6" name="On-Time Payments" data-id="element-4720" />
              <Area type="monotone" dataKey="late" stackId="1" stroke="#ef4444" fill="#ef4444" name="Late Payments" data-id="element-4721" />
            </AreaChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-4722">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-4723">
              <div className="text-center" data-id="element-4724">
                <div className="text-gray-600" data-id="element-4725">On-Time %</div>
                <div className="text-xl font-bold text-blue-600" data-id="element-4726">80.5%</div>
              </div>
              <div className="text-center" data-id="element-4727">
                <div className="text-gray-600" data-id="element-4728">Early %</div>
                <div className="text-xl font-bold text-green-600" data-id="element-4729">17.2%</div>
              </div>
              <div className="text-center" data-id="element-4730">
                <div className="text-gray-600" data-id="element-4731">Late %</div>
                <div className="text-xl font-bold text-red-600" data-id="element-4732">2.3%</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Inventory Days Trend - Full Width */}
      <Card className="p-6 mb-8" data-id="element-4733">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-4734">
          Inventory Days Trend by Category
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-4735">
          Monthly inventory holding periods (days)
        </p>
        <ResponsiveContainer width="100%" height={350} data-id="element-4736">
          <LineChart data={inventoryTrendData} data-id="element-4737">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4738" />
            <XAxis dataKey="month" tick={{
            fontSize: 12
          }} data-id="element-4739" />
            <YAxis tick={{
            fontSize: 12
          }} data-id="element-4740" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} formatter={(value: number) => `${value} days`} data-id="element-4741" />
            <Legend data-id="element-4742" />
            <Line type="monotone" dataKey="rawMaterial" stroke="#3b82f6" strokeWidth={3} name="Raw Material" dot={{
            r: 4
          }} data-id="element-4743" />
            <Line type="monotone" dataKey="wip" stroke="#f59e0b" strokeWidth={3} name="WIP" dot={{
            r: 4
          }} data-id="element-4744" />
            <Line type="monotone" dataKey="finishedGoods" stroke="#10b981" strokeWidth={3} name="Finished Goods" dot={{
            r: 4
          }} data-id="element-4745" />
            <Line type="monotone" dataKey="total" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="5 5" name="Total" dot={{
            r: 3
          }} data-id="element-4746" />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-4747">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-4748">
            <div className="text-center" data-id="element-4749">
              <div className="text-gray-600" data-id="element-4750">Raw Material Avg</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-4751">24 days</div>
            </div>
            <div className="text-center" data-id="element-4752">
              <div className="text-gray-600" data-id="element-4753">WIP Avg</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-4754">10 days</div>
            </div>
            <div className="text-center" data-id="element-4755">
              <div className="text-gray-600" data-id="element-4756">Finished Goods Avg</div>
              <div className="text-xl font-bold text-green-600" data-id="element-4757">13 days</div>
            </div>
            <div className="text-center" data-id="element-4758">
              <div className="text-gray-600" data-id="element-4759">Total Avg</div>
              <div className="text-xl font-bold text-purple-600" data-id="element-4760">47 days</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Working Capital by Region Table */}
      <Card className="p-6" data-id="element-4761">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-4762">
          Working Capital Analysis by Region
        </h3>
        <div className="overflow-x-auto" data-id="element-4763">
          <table className="w-full" data-id="element-4764">
            <thead data-id="element-4765">
              <tr className="border-b-2 border-gray-300" data-id="element-4766">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4767">
                  Region
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4768">
                  Receivables
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4769">
                  AR Days
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4770">
                  Inventory
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4771">
                  Inv Days
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4772">
                  Payables
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4773">
                  AP Days
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4774">
                  Net WC
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4775">
                  CCC
                </th>
              </tr>
            </thead>
            <tbody data-id="element-4776">
              {wcByRegionData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-4777">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-4778">
                    {row.region}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4779">
                    ₹{row.receivables} Cr
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-4780">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.receivableDays <= 58 ? 'bg-green-100 text-green-700' : row.receivableDays <= 62 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`} data-id="element-4781">
                      {row.receivableDays}d
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4782">
                    ₹{row.inventory} Cr
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-4783">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.inventoryDays <= 42 ? 'bg-green-100 text-green-700' : row.inventoryDays <= 45 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`} data-id="element-4784">
                      {row.inventoryDays}d
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4785">
                    ₹{row.payables} Cr
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-4786">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.payableDays >= 52 ? 'bg-green-100 text-green-700' : row.payableDays >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`} data-id="element-4787">
                      {row.payableDays}d
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-blue-600" data-id="element-4788">
                    ₹{row.netWC} Cr
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-4789">
                    <span className={`inline-flex px-3 py-1.5 rounded-full text-xs font-bold ${row.ccc <= 48 ? 'bg-green-100 text-green-700' : row.ccc <= 55 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`} data-id="element-4790">
                      {row.ccc} days
                    </span>
                  </td>
                </tr>)}
              {/* Total Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold" data-id="element-4791">
                <td className="py-3 px-4 text-sm text-gray-900" data-id="element-4792">TOTAL</td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-4793">
                  ₹1,850 Cr
                </td>
                <td className="py-3 px-4 text-center" data-id="element-4794">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700" data-id="element-4795">
                    58d
                  </span>
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-4796">
                  ₹1,313 Cr
                </td>
                <td className="py-3 px-4 text-center" data-id="element-4797">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700" data-id="element-4798">
                    42d
                  </span>
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-4799">
                  ₹1,473 Cr
                </td>
                <td className="py-3 px-4 text-center" data-id="element-4800">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700" data-id="element-4801">
                    52d
                  </span>
                </td>
                <td className="py-3 px-4 text-sm text-right text-blue-600" data-id="element-4802">
                  ₹1,690 Cr
                </td>
                <td className="py-3 px-4 text-center" data-id="element-4803">
                  <span className="inline-flex px-3 py-1.5 rounded-full text-xs font-bold bg-green-100 text-green-700" data-id="element-4804">
                    48 days
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-4805">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-4806">
            <div className="text-center" data-id="element-4807">
              <div className="text-gray-600" data-id="element-4808">Total Net WC</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-4809">₹1,690 Cr</div>
            </div>
            <div className="text-center" data-id="element-4810">
              <div className="text-gray-600" data-id="element-4811">Best Region</div>
              <div className="text-xl font-bold text-green-600" data-id="element-4812">West</div>
            </div>
            <div className="text-center" data-id="element-4813">
              <div className="text-gray-600" data-id="element-4814">Best CCC</div>
              <div className="text-xl font-bold text-green-600" data-id="element-4815">42 days</div>
            </div>
            <div className="text-center" data-id="element-4816">
              <div className="text-gray-600" data-id="element-4817">Target CCC</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-4818">45 days</div>
            </div>
            <div className="text-center" data-id="element-4819">
              <div className="text-gray-600" data-id="element-4820">Improvement vs LY</div>
              <div className="text-xl font-bold text-green-600" data-id="element-4821">-10 days</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default WorkingCapitalDashboard;